import type { InputHTMLAttributes, Ref } from "react";
import { useId, useState } from "react";

import type { FormFieldPresentationProps, OmittedCommonFormFieldProps } from "@core/components/FormInputWrapper";
import { FORM_INPUT_STATUSES } from "@core/components/FormInputWrapper";
import { Icon } from "@core/components/Icon";
import { IconButton } from "@core/components/IconButton";
import { TextInput } from "@core/components/TextInput";

import { LockOutlineCloseIcon } from "@jti/brand-icons/lock-outline-close";
import { UiEyeCloseIcon } from "@jti/brand-icons/ui-eye-close";
import { UiEyeOpenIcon } from "@jti/brand-icons/ui-eye-open";

import type { PasswordRequirement } from "./components/PasswordRequirements";
import { PasswordRequirements } from "./components/PasswordRequirements";

import styles from "./Password.module.scss";

export { FORM_INPUT_STATUSES as PASSWORD_STATUSES };

export type PasswordProps = FormFieldPresentationProps & {
  requirements?: PasswordRequirement[];
  showToggle?: boolean;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  pending?: boolean;
  ref?: Ref<HTMLInputElement>;
} & Omit<InputHTMLAttributes<HTMLInputElement>, OmittedCommonFormFieldProps | "type">;

export const Password = ({
  requirements,
  showToggle = true,
  showPasswordLabel = "",
  hidePasswordLabel = "",
  disabled = false,
  pending = false,
  id: externalId,
  ref,
  ...rest
}: PasswordProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const generatedId = useId();
  const inputId = externalId ?? generatedId;
  const requirementsId = requirements?.length ? `${inputId}-requirements` : undefined;

  const toggleButton = showToggle ? (
    <IconButton
      icon={isVisible ? UiEyeCloseIcon : UiEyeOpenIcon}
      aria-label={isVisible ? hidePasswordLabel : showPasswordLabel}
      aria-pressed={isVisible}
      onClick={() => setIsVisible((prev) => !prev)}
      type="button"
      disabled={disabled}
      compact
    />
  ) : undefined;

  return (
    <div className={styles.passwordWrapper}>
      <TextInput
        {...rest}
        id={inputId}
        disabled={disabled}
        type={isVisible ? "text" : "password"}
        elemBefore={<Icon icon={LockOutlineCloseIcon} size="sm" aria-hidden="true" />}
        elemAfter={toggleButton}
        aria-describedby={requirementsId}
        ref={ref}
      ></TextInput>
      {requirements && requirements.length > 0 && (
        <div id={requirementsId}>
          <PasswordRequirements requirements={requirements} pending={pending} />
        </div>
      )}
    </div>
  );
};
